import React, { type JSX } from "react";

import { get, find } from "lodash";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import type { ISite } from "@similarweb/ui-components";
import {
    SimpleLegend,
    ListItemWebsite,
    ListItemApp,
    ListItemIndustry,
    ListItemKeyword,
    ListItemKeywordGroup,
    ListItemSegment,
    SimpleLegendItemClosable,
    AutocompleteWithItems,
    WebsiteChipItem,
    CategoryChipItem,
    KeywordChipItem,
    DotsLoader,
} from "@similarweb/ui-components";

import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import type { IAutoCompleteAppItem, IAutoCompleteKeywordItem } from "app/@types/autocomplete";
import { swSettings } from "app/common/services/swSettings";
import { getDomainWithSubdomain, getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import type { IChosenItemParams } from "app/components/compare/WebsiteQueryBar/WebsitePageQueryBarUtils";
import type { IndustryEntity } from "app/components/dashboard/constants/types";
import { EFamilyTypes } from "app/components/dashboard/dashboard-templates/components/DashboardTemplatesTypes";
import { SegmentSeparator } from "app/components/dashboard/dashboard-templates/components/styledComponents";
import { getIndustrieById } from "app/components/widget/widget-types/MarketAnalysis/helpers";
import { SEGMENTS_FAMILY_NAME } from "app/components/widget/widget-utilities/segments/constants";
import { translate } from "app/filters/i18nFilter";
import { AppStoreName } from "app/pages/app-intelligence/constants";
import { itsMyOwnSegment } from "app/pages/segments/analysis/itsMyOwnSegment";
import { getCustomSegmentsMetadata } from "app/pages/segments/config/segmentsConfigHelpers";
import { selectAccountSegments, selectSegmentsMeta } from "app/pages/segments/redux/selectors";
import { autoCompleteService } from "app/services/autocomplete.service";
import { DefaultFetchService } from "app/services/fetchService";
import type { ICustomSegment, ICustomSegmentsMetaData } from "app/services/segments/segmentsApiService";
import { fetchSimilarSitesQuery } from "app/services/sitesService/queries/sitesQueries";
import type { ISimilarInfoItem } from "app/services/sitesService/types";
import { SwRedux } from "app/services/SwRedux";
import { allTrackers } from "app/services/track/track";

import { INDUSTRY_FAMILY_NAME, SUPPORTED_CATEGORIES_WITH_SEGMENTS_METRICS } from "../constants";
import { isMetricMetaDataNeeded } from "../utils/dashboardWizardUtils";

import DisabledTooltip from "./segments/components/DisabledTooltip";
import { getIsDisabledWithSegments, mergeSegmentIds, removeSegmentsFromSelected } from "./segments/helpers";
import { selectUniqueSegments } from "./segments/selectors";
import { AutocompleteItemWrapper } from "./segments/styles";
import type { ISimilarMobileAppItem } from "./types";

const DashboardWizardSimpleLegend = styled(SimpleLegend)<any>`
    flex-wrap: wrap;
    width: 100%;
    ${SimpleLegendItemClosable} {
        margin-top: 10px;
        width: 33.33%;
    }
`;
DashboardWizardSimpleLegend.displayName = "DashboardWizardSimpleLegend";

class DashboardWizardAutocomplete extends React.Component<any, any> {
    private fetchService: DefaultFetchService;

    constructor(props) {
        super(props);

        this.state = {
            maxKeys: this.getMaxKeys(),
            keyCountClass: "widgetWizard-keyCount",
            autocompleteSuggestions: [],
            autocompleteRemovedSuggestions: {},
            autocompletePlaceholder: this.getAutocompletePlaceholder(),
            isAutocompleteDisabled: this.shouldFreezeAutocomplete(props.customDashboard),
            hasFetchedMetricMetadata: false,
            isSegmentNotAvailable: false,
        };

        this.fetchService = DefaultFetchService.getInstance();
        this.getData = this.getData.bind(this);
        this.onItemSelect = this.onItemSelect.bind(this);
        this.autocompleteBlur = this.autocompleteBlur.bind(this);

        if (["Website", "Mobile"].indexOf(props.customDashboard.widget.family) > -1) {
            if (props.customDashboard.widget.key.length > 0) {
                this.getSimilarItems(props.customDashboard.widget.key[0], props.customDashboard.widget.key);
            }
        }
    }

    public componentDidUpdate(prevProps) {
        if (prevProps.customDashboard.widget !== this.props.customDashboard.widget) {
            const newWidget = this.props.customDashboard.widget;

            const keyCountClass =
                newWidget.key.length === 0 || (this.props.customDashboard.compareOnly && newWidget.key.length < 2)
                    ? "widgetWizard-keyCount widgetWizard-keyCount--error"
                    : "widgetWizard-keyCount";

            // Because of the metric drop down - we need to disable the autocomplete when props are being changed and not only in the constructor.
            this.setState({
                isAutocompleteDisabled: this.shouldFreezeAutocomplete(this.props.customDashboard),
                autocompletePlaceholder: this.getAutocompletePlaceholder(
                    newWidget.key.length,
                    this.props.customDashboard,
                ),
                keyCountClass,
            });
        }
        if (prevProps.customDashboard.widget.metric !== this.props.customDashboard.widget.metric) {
            this.setState({ hasFetchedMetricMetadata: false });
        }
    }

    getMaxKeys(customDashboard?) {
        const _customDashboard = customDashboard === undefined ? this.props.customDashboard : customDashboard;
        if (!_customDashboard.isCompare) {
            return 1;
        }

        return [INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(_customDashboard.widget.family) > -1 ? 1 : 5;
    }

    getItemIdByFamily(item, family) {
        switch (family) {
            case INDUSTRY_FAMILY_NAME:
                if (item.categoryId) {
                    return item.categoryId;
                } else {
                    return `$${item.category}`;
                }
            case "Keyword":
                if (item.groupId) {
                    return item.groupId;
                }
            default:
                return item.id;
        }
    }

    private updateSegments = (keyWithSegments: any) => {
        const {
            updateUniqueSegments,
            customDashboard: { widget },
        } = this.props;
        const reduxStore = SwRedux.getInstance().getState();
        const uniqueSegments = selectUniqueSegments(reduxStore);

        const updateOneItem = (segmentItem: Record<string, number>, segmentId: string) => {
            if (segmentItem[segmentId]) {
                segmentItem[segmentId] = segmentItem[segmentId] + 1;
            } else {
                segmentItem[segmentId] = 1;
            }
        };

        if (widget.family === INDUSTRY_FAMILY_NAME) {
            const industrySegmentsList = get(keyWithSegments, "categoryObject.segments", []);
            const segmentsList = uniqueSegments ? Object.keys(uniqueSegments) : [];

            const updatedSegments = [...segmentsList, ...industrySegmentsList].reduce((acc, segmentId) => {
                updateOneItem(acc, segmentId);
                return acc;
            }, {});

            updateUniqueSegments(updatedSegments);
            return;
        }

        const updatedUniqueSegments = {
            ...uniqueSegments,
        };

        updateOneItem(updatedUniqueSegments, keyWithSegments?.id);

        updateUniqueSegments(updatedUniqueSegments);
    };

    async onItemSelect(item) {
        //TODO: I18N;
        //Map selected item into a standard key object.
        let _newKey: any = {
            id: this.getItemIdByFamily(item, this.props.customDashboard.widget.family),
            name: item.name,
            image: item.image,
            icon: item.icon,
            type: this.props.customDashboard.widget.family,
            isSuggestion: item.isSuggestion,
        };
        const _isCompare = this.props.customDashboard.widget.key.length > 0;
        if (!_isCompare) {
            allTrackers.trackEvent(
                "Drop Down",
                "click",
                `Open ${_newKey.type}/${_newKey.isSuggestion ? "Suggested" : "Manual"}/${_newKey.name}`,
            );
        } else {
            allTrackers.trackEvent(
                "Drop Down",
                "click",
                `Add ${_newKey.type}/${_newKey.isSuggestion ? "Suggested" : "Manual"}/${_newKey.name}`,
            );
        }

        if (this.props.customDashboard.widget.family === SEGMENTS_FAMILY_NAME) {
            this.updateSegments(item);

            _newKey = {
                id: this.getItemIdByFamily(item, this.props.customDashboard.widget.family),
                name: item.segmentName,
                image: item?.image,
                icon: item.favicon,
                domain: item.domain,
                type: this.props.customDashboard.widget.family,
                isSuggestion: item.isSuggestion,
            };
        }

        //For industry items - add category prop.
        if (
            this.props.customDashboard.widget.family === INDUSTRY_FAMILY_NAME &&
            SUPPORTED_CATEGORIES_WITH_SEGMENTS_METRICS.includes(this.props.customDashboard.widget.metric)
        ) {
            this.updateSegments(item);

            _newKey.category = _newKey.id;
            _newKey.customIndustryId = item.customIndustryId;
            _newKey.isCustomCategory = !!item.customIndustryId;
        }

        if (this.props.customDashboard.widget.family === SEGMENTS_FAMILY_NAME) {
            _newKey.segmentId = _newKey.id;
        }

        //For mobile apps - add store ["google" or "apple"]
        if (item.store) {
            _newKey.store = item.store;
        }

        // in case of page filters, we need to remove the page filter as well
        if (this.props.widgetFilters.pageFilter) {
            const selectedFilters = Object.entries(this.props.widgetFilters).map(([name, value]) => ({
                name,
                value,
            }));

            const pageFilters: IChosenItemParams = JSON.parse(this.props.widgetFilters.pageFilter);
            const { subdomain, domain } = getUrlComponents(item.name);
            const domainWithSubdomain = getDomainWithSubdomain(domain, subdomain);
            pageFilters.push({
                url: domainWithSubdomain,
                searchType: subdomain ? "subdomain" : "domain",
            });

            this.props.changeFilter([...selectedFilters, { name: "pageFilter", value: JSON.stringify(pageFilters) }]);
        }
        //Dispatch keyAppend action to update Redux Store.
        this.props.keyAppend(_newKey, this.props.customDashboard);
        const _newKeys = [...this.props.customDashboard.widget.key, _newKey];

        if (
            isMetricMetaDataNeeded(this.props.customDashboard.widget.metric, this.props.customDashboard.widget.family)
        ) {
            this.props.fetchMetricMetaData(_newKeys, this.props.customDashboard);
            this.setState({ hasFetchedMetricMetadata: true });
        }
        if (this.props.customDashboard.widget.family === "Website") {
            setTimeout(() => {
                this.props.updateGAVerifiedDataFilter(_newKeys, this.props.customDashboard);
            });
        }

        //If the selected item is the first - get similar-sites\apps for it.
        if (this.props.customDashboard.widget.key.length === 0) {
            if (this.shouldFreezeAutocomplete(this.props.customDashboard)) {
                this.setState({
                    autocompletePlaceholder: this.getAutocompletePlaceholder(_newKeys.length),
                    isAutocompleteDisabled: true,
                });
                return;
            }
            if (!([INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(this.props.customDashboard.widget.family) > -1)) {
                this.getSimilarItems(item);
            }
        }
        //Otherwise - remove the selected item from suggestions list (to avoid selecting the same suggestion twice)/
        else {
            this.removeItemFromSuggestions(item);
        }

        //Compare is not allowed for industry and keyword modules.
        if ([INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(this.props.customDashboard.widget.family) > -1) {
            this.setState({
                autocompletePlaceholder: this.getAutocompletePlaceholder(_newKeys.length),
                isAutocompleteDisabled: true,
            });
            return;
        }
        //Max compared items is 5 (primary site + 4).
        if (this.props.customDashboard.widget.key.length >= 4) {
            this.setState({
                autocompletePlaceholder: this.getAutocompletePlaceholder(),
                isAutocompleteDisabled: true,
            });
            return;
        }

        //After selection - clear search input's value - and suggest adding competitors from similarSites.
        this.setState({
            autocompletePlaceholder: this.getAutocompletePlaceholder(),
        });
    }

    listItemByFamily(family, item) {
        switch (family) {
            case EFamilyTypes.RankTracker:
            case EFamilyTypes.Advertiser:
            case EFamilyTypes.Publisher:
            case "Website":
                return (
                    <ListItemWebsite
                        img={item.image}
                        key={item.name}
                        onClick={() => this.onItemSelect(item)}
                        text={item.name}
                    />
                );
            case INDUSTRY_FAMILY_NAME:
                return item?.isDisabled ? (
                    <AutocompleteItemWrapper>
                        <ListItemIndustry
                            iconClassName={item.icon}
                            key={item.name}
                            onClick={() => this.onItemSelect(item)}
                            text={item.name}
                            disabled={item.isDisabled}
                            tooltipText={item.tooltipText}
                        />
                        <DisabledTooltip
                            tooltipText={
                                item?.isSegmentsDisabled
                                    ? "dashboard.wizard.categories.autocomplete.item.disable.due.to.segments"
                                    : "dashboard.wizard.categories.autocomplete.item.industry.segments.limit.tooltip"
                            }
                            isGroup={false}
                        />
                    </AutocompleteItemWrapper>
                ) : (
                    <ListItemIndustry
                        iconClassName={item.icon}
                        key={item.name}
                        onClick={() => this.onItemSelect(item)}
                        text={item.name}
                        disabled={item.isDisabled}
                        tooltipText={item.tooltipText}
                    />
                );
            case "Keyword":
                // keyword group
                if (item.groupId) {
                    return (
                        <ListItemKeywordGroup
                            key={item.name}
                            onClick={() => this.onItemSelect(item)}
                            text={item.name}
                        />
                    );
                } else {
                    return (
                        <ListItemKeyword
                            key={item.name}
                            onClick={() => this.onItemSelect(item)}
                            text={item.name}
                        />
                    );
                }
            case "Mobile":
                return (
                    <ListItemApp
                        img={item.image}
                        store={item.store}
                        key={item.name}
                        onClick={() => this.onItemSelect(item)}
                        text={item.name}
                        subtitle={item.publisher}
                    />
                );
            case SEGMENTS_FAMILY_NAME:
                return item?.isDisabled ? (
                    <AutocompleteItemWrapper>
                        <ListItemSegment
                            segmentName={item.segmentName}
                            domainName={item.domain}
                            img={item.favicon}
                            withSegmentSign
                            isDisabled
                        />
                        <DisabledTooltip isGroup={false} />
                    </AutocompleteItemWrapper>
                ) : (
                    <ListItemSegment
                        segmentName={item.segmentName}
                        domainName={item.domain}
                        img={item.favicon}
                        withSegmentSign
                        isDisabled={item?.isDisabled}
                        onClick={() => this.onItemSelect(item)}
                    />
                );
        }
    }

    private isSegmentEntityDisabled = (id: string, segmentIds?: string[]) => {
        const uniqueSegmentIds = new Set(segmentIds);

        if (uniqueSegmentIds) {
            const { SegmentsPerDashboardHardLimit } = swSettings?.current?.resources;

            const usedSegmentsNum = uniqueSegmentIds.size;

            if (usedSegmentsNum < SegmentsPerDashboardHardLimit) {
                return false;
            }

            if (usedSegmentsNum === SegmentsPerDashboardHardLimit) {
                return !uniqueSegmentIds.has(id);
            }

            if (usedSegmentsNum > SegmentsPerDashboardHardLimit) {
                return false;
            }
        }

        return false;
    };

    private prepareSegmentsWithSearch = (segments: ICustomSegment[], query: string, family: string) => {
        if (segments) {
            const reduxStore = SwRedux.getInstance().getState();
            const uniqueSegments = selectUniqueSegments(reduxStore);

            let segmentIdList = [];

            if (uniqueSegments) {
                segmentIdList = Object.keys(uniqueSegments);
            }

            return segments.reduce((acc, segment) => {
                const isSegmentNameEqual = segment?.segmentName.toLowerCase().includes(query.toLowerCase());

                const isSegmentDomainEqual = segment?.domain.toLowerCase().includes(query.toLowerCase());

                if (isSegmentNameEqual || isSegmentDomainEqual) {
                    segment.isDisabled = this.isSegmentEntityDisabled(segment.id, segmentIdList);

                    const transformed = this.listItemByFamily(family, segment);

                    acc.push(transformed);
                }
                return acc;
            }, []);
        }

        return [];
    };

    private transformSegmentsDataToAutocomplete = (_family: string, query: string, data?: ICustomSegmentsMetaData) => {
        if (data) {
            const segments = this.prepareSegmentsWithSearch(data?.Segments, query, _family);
            let accountSegments = [];

            if (data.AccountSegments) {
                const filteredSegments = data.AccountSegments.filter((s) => !itsMyOwnSegment(s?.userId));

                accountSegments = this.prepareSegmentsWithSearch(filteredSegments, query, _family);
            }

            return [
                <SegmentSeparator key="separator-my-segments">My Segments</SegmentSeparator>,
                ...segments,
                <SegmentSeparator key="separator-account-segments">Account Segments</SegmentSeparator>,
                ...accountSegments,
            ];
        }

        return [];
    };

    private getIndustryIdFromList = (industry: IndustryEntity) => {
        const {
            categoryId,
            categoryObject: { isCustomCategory },
        } = industry;
        return isCustomCategory ? categoryId : undefined;
    };

    private getIndustryItems = (family: string, query?: string) =>
        new Promise(async (resolve) => {
            const _results: IndustryEntity[] = await autoCompleteService.getCategoriesSection(query);

            const isSegmentsAvailable = SUPPORTED_CATEGORIES_WITH_SEGMENTS_METRICS.includes(
                this.props.customDashboard.widget.metric,
            );

            const reduxStore = SwRedux.getInstance().getState();
            const uniqueSegments = selectUniqueSegments(reduxStore);

            const enrichedIndustries = _results.map((category) => {
                const customIndustryId = isSegmentsAvailable && this.getIndustryIdFromList(category);

                const categorySegments = category?.categoryObject?.segments ?? [];
                const { isCustomCategory } = category?.categoryObject;

                const isDisabledWithoutSegments = isCustomCategory ? !!categorySegments.length : false;
                const mergedSegments = mergeSegmentIds(uniqueSegments, categorySegments);

                const isDisabled = isSegmentsAvailable
                    ? getIsDisabledWithSegments(mergedSegments)
                    : isDisabledWithoutSegments;

                const enrichedItem = {
                    ...category,
                    isSegmentsDisabled: !isSegmentsAvailable,
                    isDisabled: isDisabled,
                    ...(customIndustryId && { customIndustryId }),
                };
                return this.listItemByFamily(family, enrichedItem);
            });

            resolve(enrichedIndustries);
        });

    //TODO consider extracting to external service or adding to autocomplete service
    async getData(query?: string) {
        const _family = this.props.customDashboard.widget.family;
        const _getDataToken = Math.random();
        const _isCompare = this.props.customDashboard.widget.key.length > 0;
        let _url = "";
        const _userKeywordsGroups: IAutoCompleteKeywordItem[] = [];
        let _store = null;

        switch (_family) {
            case "Website":
            case EFamilyTypes.RankTracker:
            case EFamilyTypes.Advertiser: // To change once we have new endpoint
            case EFamilyTypes.Publisher: // To change once we have new endpoint
                _url = "websites";
                break;
            case "Mobile":
                _url = "apps";
                if (this.props.customDashboard.widget.key.length) {
                    _store = this.props.customDashboard.widget.key[0].store;
                } else {
                    _store = this.props.customDashboard.androidOnly
                        ? "google"
                        : this.props.customDashboard.iosOnly
                          ? "Apple"
                          : undefined;
                }
                break;
            case "Keyword":
                _url = "keywords";
                break;
            case SEGMENTS_FAMILY_NAME:
                break;
        }

        if (typeof query === "string" && query != "") {
            if (_family === SEGMENTS_FAMILY_NAME) {
                return new Promise(async (resolve) => {
                    const reduxStore = SwRedux.getInstance().getState();
                    const accountSegments = selectAccountSegments(reduxStore);

                    if (!accountSegments) {
                        const metaData = await getCustomSegmentsMetadata();

                        const transformedData = this.transformSegmentsDataToAutocomplete(_family, query, metaData);

                        resolve(transformedData);
                        return;
                    }

                    const segmentsMeta = selectSegmentsMeta(reduxStore);

                    const transformedSegments = this.transformSegmentsDataToAutocomplete(_family, query, segmentsMeta);

                    resolve(transformedSegments);
                });
            }
            if (_family === INDUSTRY_FAMILY_NAME) {
                return this.getIndustryItems(_family, query);
            }
            if (_family === "Keyword") {
                const userGroups = await keywordGroupsQueryClient.getUserGroups();
                userGroups.forEach((group) => {
                    if (group.Name.toLowerCase().indexOf(query.toLowerCase()) > -1) {
                        _userKeywordsGroups.push({
                            type: "keyword",
                            name: group.Name,
                            id: "*" + group.Name,
                            groupId: group.Id,
                            isVirtual: false,
                            icon: "icon sw-icon-folder",
                            url: group.url,
                        });
                    }
                });
                const _listItems = [];
                _userKeywordsGroups.map((item) => {
                    //Enable site\app selection only if not already exists in the widget's key object.
                    _listItems.push(this.listItemByFamily(_family, item));
                });
                if (this.props.customDashboard.widget.metric.indexOf("KeywordAnalysisGroup") > -1) {
                    return _listItems;
                }
            }

            this.setState({
                getDataToken: _getDataToken,
            });
            const queryParams = {
                size: 9,
                term: query,
                store: _store,
            };
            return this.fetchService.get(`/autocomplete/${_url}`, queryParams).then(
                (results: Array<ISite | IAutoCompleteAppItem>) => {
                    if (!_isCompare) {
                        allTrackers.trackEvent("Drop Down", "open", `Open ${_family}`);
                    } else {
                        allTrackers.trackEvent("Drop Down", "open", `Add ${_family}`);
                    }
                    //Apply fetch data only if it is the last one triggered.
                    //We insure that by comparing the private _getDataToken to the state.getDataToken.
                    if (_getDataToken === this.state.getDataToken) {
                        if (_userKeywordsGroups.length) {
                            results.unshift(..._userKeywordsGroups);
                        }
                        const listItems = [];
                        results.map((item) => {
                            //Enable site\app selection only if not already exists in the widget's key object.
                            if (
                                !find(this.props.customDashboard.widget.key, {
                                    name: item.name,
                                })
                            ) {
                                listItems.push(this.listItemByFamily(_family, item));
                            }
                        });
                        return listItems;
                    }
                },
                (error) => {
                    Sentry.error("Dashboard Wizard Autocomplete, fetch items:", error);
                },
            );
        } else {
            if (this.props.customDashboard.widget.key.length > 0) {
                allTrackers.trackEvent("Drop Down", "open", `Add ${_family}`);
                return this.state.autocompleteSuggestions;
            } else {
                return [];
            }
        }
    }

    getSimilarItems(key, excludeList?) {
        let promise: Promise<ISimilarInfoItem[] | ISimilarMobileAppItem[]> = new Promise((resolve) => resolve([]));
        switch (this.props.customDashboard.widget.family) {
            case "Website":
                promise = fetchSimilarSitesQuery(key.name as string, { limit: 20 });
                break;
            case "Mobile": {
                const url = `/api/MobileApps/GetSimilarApps?appId=${key.id}&store=${key.store}&limit=20`;
                promise = this.fetchService.get<ISimilarMobileAppItem[]>(url);
                break;
            }
        }
        return promise.then(
            (_results) => {
                const _listItems = [];
                _results.map((item) => {
                    const _newKey: any = {
                        id: item.Domain || item.ID,
                        name: item.Domain || item.Title,
                        image: item.Favicon || item.Icon,
                        isSuggestion: true,
                        publisher: item.Author,
                        type: this.props.customDashboard.widget.family,
                    };
                    if (item.Store !== undefined) {
                        const _store = item.Store === 0 ? AppStoreName.GOOGLE : AppStoreName.APPLE;
                        _newKey.store = _store;
                    }
                    if (!excludeList || !find(excludeList, { name: _newKey.name })) {
                        _listItems.push(this.listItemByFamily(this.props.customDashboard.widget.family, _newKey));
                    }
                });
                this.setState({ autocompleteSuggestions: _listItems });
            },
            (e) => {
                return e;
            },
        );
    }

    removeKeyItem = (selectedItem) => () => {
        allTrackers.trackEvent("Drop Down", "click", `Remove ${selectedItem.type}/${selectedItem.name}`);

        // in case of page filters, we need to remove the page filter as well
        if (this.props.widgetFilters.pageFilter) {
            const selectedFilters = Object.entries(this.props.widgetFilters).map(([name, value]) => ({
                name,
                value,
            }));

            const pageFilters: IChosenItemParams = JSON.parse(this.props.widgetFilters.pageFilter);
            // remove selectedItem from pageFilters
            const newPageFilters = pageFilters.filter((item) => item.url !== selectedItem.name);

            this.props.changeFilter([
                ...selectedFilters,
                { name: "pageFilter", value: JSON.stringify(newPageFilters) },
            ]);
        }
        this.props.keyRemove(selectedItem, this.props.customDashboard);

        const metric = get(this.props, "customDashboard.widget.metric", "");
        const key = get(this.props, "customDashboard.widget.key", "");
        const family = get(this.props, "customDashboard.widget.family", "");

        const _newKeys = key.filter((keyItem) => {
            return keyItem.name !== selectedItem.name;
        });

        if (isMetricMetaDataNeeded(metric, family)) {
            this.props.fetchMetricMetaData(_newKeys, this.props.customDashboard);
            this.setState({ hasFetchedMetricMetadata: true });
        }

        if (family === "Website") {
            setTimeout(() => {
                this.props.updateGAVerifiedDataFilter(_newKeys, this.props.customDashboard);
            });
        }

        if (family === SEGMENTS_FAMILY_NAME) {
            const reduxStore = SwRedux.getInstance().getState();
            const segments = selectUniqueSegments(reduxStore);

            const updatedSegments = removeSegmentsFromSelected(selectedItem.segmentId, segments);
            this.props.updateUniqueSegments(updatedSegments);
        }

        if (family === INDUSTRY_FAMILY_NAME && SUPPORTED_CATEGORIES_WITH_SEGMENTS_METRICS.includes(metric)) {
            const reduxStore = SwRedux.getInstance().getState();
            const segments = selectUniqueSegments(reduxStore);

            const segmentsFromCategory = get(selectedItem, "categoryObject.segments", undefined);
            const categorySegments = segmentsFromCategory
                ? segmentsFromCategory
                : getIndustrieById(`*${selectedItem.id}`)?.segments;

            const updatedSegments = removeSegmentsFromSelected(categorySegments, segments);

            this.props.updateUniqueSegments(updatedSegments);
        }

        // We remove sites from the suggestions list after we add them to comparison mode.
        // This code adds the site back to the suggestion list
        // After we remove the site from the comparison.
        this.setState((prevState) => {
            if (prevState.autocompleteRemovedSuggestions[selectedItem.name]) {
                const _removed = prevState.autocompleteRemovedSuggestions;
                const _suggestions = prevState.autocompleteSuggestions;
                _suggestions.unshift(_removed[selectedItem.name]);
                delete _removed[selectedItem.name];

                return {
                    autocompleteSuggestions: _suggestions,
                    autocompleteRemovedSuggestions: _removed,
                    autocompletePlaceholder: this.getAutocompletePlaceholder(_newKeys.length),
                    isAutocompleteDisabled: false,
                };
            } else {
                return {
                    autocompletePlaceholder: this.getAutocompletePlaceholder(_newKeys.length),
                    isAutocompleteDisabled: false,
                };
            }
        });

        this.autocompleteBlur(key.length - 1);
    };

    renderKeyTags() {
        const _family = this.props.customDashboard.widget.family;
        const _keys = this.props.customDashboard.widget.key;
        const shouldFetchMetricMetaData =
            _keys.length &&
            isMetricMetaDataNeeded(
                this.props.customDashboard.widget.metric,
                this.props.customDashboard.widget.family,
            ) &&
            !this.state.hasFetchedMetricMetadata;
        if (shouldFetchMetricMetaData) {
            this.props.fetchMetricMetaData(_keys, this.props.customDashboard);
            this.setState({ hasFetchedMetricMetadata: true });
        }
        if (_family === SEGMENTS_FAMILY_NAME) {
            return _keys.map((key) => (
                <WebsiteChipItem
                    key={key?.id}
                    text={key.segmentName || key.name}
                    onCloseItem={this.removeKeyItem(key)}
                    image={key?.icon || key?.favicon || key?.image}
                />
            ));
        }
        if ([INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(_family) > -1) {
            const _tags = [];
            _keys.forEach((item, i) => {
                if (item) {
                    const Component = _family === "Keyword" ? KeywordChipItem : CategoryChipItem;
                    _tags.push(
                        <Component
                            text={item.name}
                            onCloseItem={this.removeKeyItem(item)}
                        />,
                    );
                }
            });
            return _tags;
        } else {
            const pageFilter = this.props.customDashboard.widget.filters?.pageFilter;
            const hideActionButton =
                pageFilter !== undefined && JSON.parse(pageFilter).some((website) => website.searchType !== "domain");
            return _keys.map((key, index) => {
                return (
                    <WebsiteChipItem
                        key={`key-${index}`}
                        text={key.name}
                        onCloseItem={this.removeKeyItem(key)}
                        image={key.image}
                        hideActionButton={hideActionButton}
                    />
                );
            });
        }
    }

    autocompleteBlur(keyLength?) {
        setTimeout(() => {
            if (keyLength) {
                this.setState({
                    autocompletePlaceholder: this.getAutocompletePlaceholder(keyLength),
                });
            } else {
                //length is required as we are expecting to a number.
                //if the length == 0 it means that we have no selected items and this is our first item
                //so the text should be different.
                this.setState({
                    autocompletePlaceholder: this.getAutocompletePlaceholder(
                        this.props.customDashboard.widget?.key?.length,
                    ),
                });
            }
        }, 300);
    }

    removeItemFromSuggestions(item) {
        this.setState((prevState) => {
            const _removed = prevState.autocompleteRemovedSuggestions;
            const _suggestions = prevState.autocompleteSuggestions.filter((listItem) => {
                const _toRemove = item.name == listItem.key;
                if (_toRemove) {
                    _removed[listItem.key] = listItem;
                }
                return !_toRemove;
            });
            return {
                autocompleteSuggestions: _suggestions,
                autocompleteRemovedSuggestions: _removed,
            };
        });
    }

    shouldFreezeAutocomplete(customDashboard) {
        const isCompare = customDashboard.isCompare;
        const keyLength = customDashboard.widget.key.length;
        const pageFilter = customDashboard.widget.filters?.pageFilter;
        const hasPageFilter =
            pageFilter !== undefined && JSON.parse(pageFilter).some((website) => website.searchType !== "domain");
        const isNoCompareFamily = [INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(customDashboard.widget.family) > -1;
        if (hasPageFilter) {
            return true;
        }
        // If new metric not allows compare and a website is selected - freeze the autocomplete.
        if (keyLength > 0) {
            if (!isCompare || isNoCompareFamily || keyLength >= this.getMaxKeys(customDashboard)) {
                return true;
            }
        }
        return false;
    }

    getAutocompletePlaceholder(keyLength?, customDashboard?) {
        const _customDashboard = customDashboard === undefined ? this.props.customDashboard : customDashboard;
        const _keyLength = keyLength === undefined ? _customDashboard.widget.key.length : keyLength;
        const _family = _customDashboard.widget.family;

        if ([INDUSTRY_FAMILY_NAME, "Keyword"].indexOf(_family) > -1) {
            return `${translate(`home.dashboards.wizard.compare.not.allowed.for`)} ${translate(
                `home.dashboards.modules.${_family}`,
            )}`;
        }
        if (_keyLength === 0) {
            return `${translate(`home.dashboards.wizard.search`)} ${translate(`home.dashboards.modules.${_family}`)}`;
        }
        if (_family === SEGMENTS_FAMILY_NAME && _keyLength === 1 && !_customDashboard.isCompare) {
            return translate(`home.dashboards.wizard.info.message.no.compare`);
        }
        if (_keyLength === 1 && !_customDashboard.isCompare) {
            return translate(`home.dashboards.wizard.compare.not.allowes`);
        }
        if (_keyLength >= this.getMaxKeys(_customDashboard)) {
            return translate(`home.dashboards.wizard.max.items.error`);
        }
        if (_keyLength > 0 && _customDashboard.compareOnly) {
            return translate(`home.dashboards.wizard.compareOnly.competitorsRequired`);
        }
        if (_keyLength >= 1 && _keyLength < this.getMaxKeys(_customDashboard)) {
            return translate(`home.dashboards.wizard.add.competitors`);
        }
    }

    render() {
        return (
            <div className="widgetWizard-autoComplete">
                <h4>{translate(`home.dashboards.modules.${this.props.customDashboard.widget.family}`)}</h4>
                <span className={this.state.keyCountClass}>
                    {this.props.customDashboard.widget.key.length}/{this.state.maxKeys}
                </span>
                <div className={`${this.props.customDashboard.widget.family} widgetWizard-autoCompleteContainer`}>
                    <AutocompleteWithItems
                        loadingComponent={<DotsLoader />}
                        maxResults={10}
                        debounce={400}
                        disabled={this.state.isAutocompleteDisabled}
                        placeholder={this.state.autocompletePlaceholder}
                        onBlur={this.autocompleteBlur}
                        getListItems={this.getData}
                        selectedItems={this.renderKeyTags() as JSX.Element[]}
                        floating={true}
                        zIndex={9}
                    />
                </div>
            </div>
        );
    }
}

export default DashboardWizardAutocomplete;
